import React from 'react'
import { message, Upload, Input, Button, Radio } from 'antd'
import { RcFile, UploadFile, UploadChangeParam } from 'antd/lib/upload/interface'
import { RadioChangeEvent } from 'antd/es/radio'
import { InboxOutlined } from '@ant-design/icons'
import * as API from '../../../../api'
import * as UTILS from '../../../../utils'
import './index.less'

type ContentFile = string;

export type FileType = '音频' | '文章' | '视频' | '图片' | string;

interface WebGrabInfo {
	picSrc: string | null;
	text: string | null;
	[propName: string]: any;
}

export interface ContentUploadDraggerProps {
	value?: ContentFile; // 文件链接
	onChange?: (value?: ContentFile, file?: RcFile, type?: FileType) => void;
}

export interface ContentUploadDraggerState {
	fileSelected?: RcFile;
	fileUrl: ContentFile;
	fileList: UploadFile[];
	webUrl: string;
	webGrablist: WebGrabInfo[];
	webGrabUrl: string;
	searchLoading: boolean;
	fileType: FileType;
}

const initFileList = (url: string) => {
	// 注：upload组件 defaultFileList 不能和 fileList 一起使用
	return [{
		uid: url,
		url: url,
		name: url,
		size: 100,
		type: '',
	}]
}

class ContentUploadDragger extends React.Component<ContentUploadDraggerProps, ContentUploadDraggerState> {
	state: ContentUploadDraggerState = {
		fileUrl: this.props.value || '',
		fileList: this.props.value ? initFileList(this.props.value) : [],
		webUrl: '',
		webGrablist: [],
		webGrabUrl: '',
		searchLoading: false,
		fileType: '',
	}

	grabFromWeb = (val: string, e): void => {
		this.preventOpenFileDialogOnClick(e)
		const reg = /(https?|ftp|file):\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|]/
		if (!val) {
			message.warning('网址链接不能为空')
			return
		}
		if (!reg.test(val)) {
			message.error('请输入正确的网络链接')
			return
		}
		this.getWebGrabList(val)
	}

	getWebGrabList = async (url: string) => {
		this.setState({
			searchLoading: true,
		})
		try {
			const { code, data, msg } = await API.grabFromWeb({ url })
			this.setState({ searchLoading: false })
			if (code === '200') {
				if (!data) {
					message.warning('暂无内容')
					return
				}
				const list = data.picData
				let text = ''
				data.textData.map(item => text += item.text)
				list.push({
					id: null,
					picSrc: null,
					text,
				})
				this.setState({
					webGrablist: list,
				})
			} else {
				message.error(msg)
			}
		} catch (error) {
			message.error('提取失败')
			this.setState({
				searchLoading: false,
			})
		}
	}

	handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		this.setState({ webUrl: e.target.value })
	}

	onWebGrabUrlChange = (e: RadioChangeEvent): void => {
		const webGrabUrl = e.target.value
		this.setState({
			webGrabUrl,
			fileType: e.target.name,
		}, () => { // 同步更新
			this.triggerChange(webGrabUrl)
		})
	}

	switchLocalUpload = (): void => {
		this.setState({
			webUrl: '',
			webGrablist: [],
		})
	}

	beforeUpload = (file: RcFile): boolean => {
		this.setState({ fileSelected: file })
		if (this.state.fileList.length === 0) return true

		// 只能上传一个文件, 多次上传将进行替换
		this.setState({ fileList: [file] })
		return true
	}

	handleChange = ({ file, fileList }: UploadChangeParam): void => {
		const { status, response } = file
		if (status === 'done') {
			if (response.code !== 0) {
				message.error(response.msg || '文件上传失败')
				return
			}
			const updateUrl = response.data
			this.setState({
				fileUrl: updateUrl,
				fileType: UTILS.getFileType(file.name)
			}, () => {
				this.triggerChange(updateUrl)
			})
		}

		this.setState({ fileList })
	}

	onRemove = (file) => {
		const { onChange } = this.props
		onChange && onChange('', null, '')
	}

	triggerChange = (fileUrl: ContentFile): void => {
		const { onChange } = this.props
		const { fileSelected, fileType } = this.state
		onChange && onChange(fileUrl, fileSelected, fileType)
	}

	/**
	 * 阻止点击打开对话框
	 */
	preventOpenFileDialogOnClick = (e) => {
		UTILS.stopPropagation(e)
	}

	render() {
		const { fileList, webUrl, webGrablist, webGrabUrl, searchLoading } = this.state
		return (
			<Upload.Dragger
				className="content-upload-drag-box"
				action={`${process.env.REACT_APP_BASE_URL}/infoManage/addFile`}
				fileList={fileList}
				withCredentials={true}
				beforeUpload={this.beforeUpload}
				onChange={this.handleChange}
				onRemove={this.onRemove}
				openFileDialogOnClick={webGrablist.length ? false : true}
			>
				{webGrablist.length
					? <>
						<div className="weburl-thumbnails">
							<Radio.Group name="图片" onChange={this.onWebGrabUrlChange} value={webGrabUrl}>
								{webGrablist.map((info, i) => (
									info.picSrc && <Radio className="pic-radio" key={i} value={info.picSrc}>
										<img className="thumb" src={info.picSrc}  alt={info.picSrc}/>
									</Radio>
								))}
							</Radio.Group>
							<Radio.Group name="文章" onChange={this.onWebGrabUrlChange} value={webGrabUrl}>
								{webGrablist.map((info, i) => (
									info.text && <Radio className="text-radio" key={i} value={info.text}>{info.text}</Radio>
								))}
							</Radio.Group>
						</div>
					</>
					: <>
						<p className="upload-drag-icon">
							<InboxOutlined />
						</p>
						<p className="upload-text">
							<span>将文件拖拽到此处或</span>
							<Button type="link" className="click">点击上传</Button>
						</p>
						<p className="upload-text">*上传素材,格式不限,总素材的大小上限为50M</p>
						<p className="divide">or</p>
					</>
				}

				<div style={{display: 'flex'}}>
					<Input.Search
						className="weburl-input"
						placeholder="请输入网址链接"
						enterButton="从网页中提取"
						value={webUrl}
						loading={searchLoading}
						onChange={this.handleInputChange}
						onSearch={this.grabFromWeb}
						onClick={this.preventOpenFileDialogOnClick}
					/>
					{webGrablist.length > 0 && <Button className="local-upload-btn" onClick={this.switchLocalUpload}>本地上传</Button>}
				</div>
			</Upload.Dragger>
		)
	}
}

export default ContentUploadDragger